import { appUrl } from '@kkf/design'
import { LINKS } from './links'
import { CROWN_ICON, FLEUR_ICON } from './royal-icons'
import { SERVICES as SERVICE_LIST } from './services'

/**
 * Content for the index page. Copy is drawn from `docs/resources/Case for Support`.
 *
 * The capital ask from that document is on the site now, as an appeal on `/donations` —
 * see `initial-capital-investment` in `data/donations`. It was on the about page until
 * 2026-09-19. The home page points at it rather than restating it.
 */

const BANNER_SLIDE = {
  subTitle: { en: 'King of Kings Foundation · Mongolia', mn: 'King of Kings Foundation · Монгол' },
  title: { en: 'For Lives Worth Living', mn: 'Үнэ цэнтэй амьдралын төлөө' },
  descr:
    { en: 'Partnering with you on your journey toward an abundant life in Christ. Through mentoring, collaborative partnerships, and a dedicated Christian resort and off-road garage, we provide a supportive community where you can truly thrive.', mn: 'Христ доторх элбэг дэлбэг амьдрал руу чиглэсэн таны замд хамт явна. Зөвлөгөө, хамтын түншлэл, мөн Христийн амралтын газар, замын бус талбайгаар дамжуулан бид таныг үнэхээр цэцэглэн хөгжих дэмжлэгт хамт олныг бүрдүүлдэг.' },
  ctaLabel: { en: 'Donate Now', mn: 'Одоо хандивлах' },
  ctaTo: '/donations',
}

/**
 * One image, so the hero is a plain banner rather than a carousel — `BannerSlider`
 * renders it without swiper, thumbs or arrows when there is a single slide.
 *
 * The lodge, ~1280px wide against a 1920 design, so it upscales slightly on very large
 * displays; a higher-resolution original drops straight in.
 */
export const BANNER = {
  slides: [
    {
      ...BANNER_SLIDE,
      image: '/assets/img/banner-lodge-winter.jpg',
      alt: { en: 'A timber lodge under deep snow, backed by a snow-covered pine forest', mn: 'Зузаан цасан доорх модон байшин, ард нь цасанд дарагдсан нарс ой' },
    },
  ],
  thumbs: [],
  prevArrow: '/assets/img/left-arrow.svg',
  nextArrow: '/assets/img/right-arrow.svg',
}

/**
 * The two asks at the top of `/donations`: join Partners, or give.
 *
 * **It was "Become A Volunteer" until 2026-09-19**, and the button pointed at `'#'` — an
 * ask with nowhere to go, which is the same mistake as a form that appears to send and does
 * not. Partners is the ask that has a real destination, a real application behind it and
 * a real answer at the other end, so it takes the slot.
 *
 * **The three bullets are Partners' own promises, restated.** Ground rule 1 forbids
 * this app importing from `app_partners`, so they are copied — which means they can drift,
 * and a benefit promised here and not there is a promise the Foundation cannot keep. They
 * come from `app_partners/src/data/partners.ts`'s `PARTNERS_JOIN.benefits` and
 * `PARTNERS_WHY.benefits`; change one and check the other. Nothing here is invented: there
 * is no member fee named, no member count and no waiting time, because none of the three
 * has been decided.
 */
export const PARTNERS_AND_GIVE = {
  left: {
    title: { en: 'Join King of Kings Partners', mn: 'King of Kings Partners-д элсэх' },
    descr:
      { en: 'A directory of Mongolian businesses that train young people and serve their community. We meet every business before listing it, and members get more back than a line in a list.', mn: 'Залуучуудыг сургаж, олон нийтдээ үйлчилдэг Монголын бизнесүүдийн лавлах. Бид бизнес бүрийг бүртгэхийн өмнө уулздаг бөгөөд гишүүд жагсаалтын нэг мөрөөс илүүг авдаг.' },
    list: [
      { en: '5% off the resort, the halls, the library and guest rooms — for you and your staff', mn: 'Амралтын газар, танхим, номын сан, зочны өрөөнд 5% хөнгөлөлт — танд болон таны ажилтнуудад' },
      { en: 'A listing in the directory, and referrals when someone asks who to call', mn: 'Лавлах дахь зар, мөн хэн нэгэн хэнд хандахаа асуувал санал болголт' },
      { en: 'First call on apprentices and graduates from the garage and our workshops', mn: 'Гараж болон манай сургалтын дадлагажигч, төгсөгчдийг эхэлж сонгох эрх' },
    ],
    ctaLabel: { en: 'Apply To Join', mn: 'Элсэх хүсэлт гаргах' },
    ctaHref: appUrl(LINKS, 'partners', '/join'),
  },
  right: {
    title: { en: 'Give To The Work', mn: 'Ажилд дэмжлэг үзүүлэх' },
    descr:
      { en: 'Revenue and donations support youth development, and keep retreats, camps and community events accessible — including free programs for those who could not otherwise come.', mn: 'Орлого, хандив нь залуучуудын хөгжлийг дэмжиж, цугларалт, зуслан, олон нийтийн арга хэмжээг хүртээмжтэй байлгадаг — үүнд өөрөөр ирж чадахгүй хүмүүст зориулсан үнэгүй хөтөлбөр багтана.' },
    ctaLabel: { en: 'Donate Now', mn: 'Одоо хандивлах' },
  },
}

/**
 * The pull quote over the About band — **a labelled placeholder, awaiting Brad Smith.**
 *
 * It carried Dr Rob Martin's words until 2026-09-21. They are real and were used precisely
 * because nothing about them was invented; the Foundation has asked for Brad Smith's
 * quotation in that position instead, and it has not been supplied yet.
 *
 * **So this says so on the page, rather than holding a quotation nobody asked for.** The
 * platform's rule for content that is not final is that it is labelled and not hidden — the
 * garage's three testimonies each say "testimony to be recorded" in their attribution, and
 * Partners' eight members say to replace them wholesale. This is the same shape: the band
 * stays, so the page's rhythm is what it will be, and the words say what is missing and who
 * it is from.
 *
 * `git log -- app_website/src/data/home.ts` has Rob Martin's words and their Mongolian if
 * they are ever wanted back. They were `RESORT_QUOTE` in `app_resort/src/data/resort.ts`
 * before that.
 *
 * **Replace `text` and `attribution` together**, and delete the `pending` flag with them —
 * `HomePage` keys the placeholder styling off it, and a real quotation left wearing it
 * would be a real person's words dressed as a stand-in. `app_website/tests` pins that the
 * placeholder is either fully here or fully gone.
 */
export const HOME_QUOTE = {
  text: {
    en: 'Quotation to be supplied — a few words from Brad Smith go here.',
    mn: 'Ишлэлийг ирүүлэхээр хүлээж байна — Брэд Смитийн хэдэн үг энд орно.',
  },
  attribution: 'Brad Smith — quotation to be supplied',
  /**
   * True while the words above are a stand-in.
   *
   * A flag rather than a convention, because "is this real?" is a question the page has to
   * answer visually and no amount of reading the string can decide. Remove it in the same
   * commit as the real quotation.
   */
  pending: true,
}

export const HOME_ABOUT = {
  image: '/assets/img/resort-campus-aerial.jpg',
  imageAlt: { en: 'The resort seen from above: timber buildings among trees, hills beyond', mn: 'Амралтын газар дээрээс харахад: моддын дунд модон барилгууд, цаана нь толгод' },
  /**
   * The badge used to read "7 / Areas Of Our Work", counting the case for support's
   * response areas — a number that means nothing to a first-time visitor and appears
   * nowhere else on the site. It now names the three strands the Foundation actually
   * runs, and it links to the page that explains them.
   */
  stat: {
    number: '3',
    txt: 'Strands, one work: the resort, the garage, Partners',
    to: '/about',
  },
  subTitle: { en: 'About US', mn: 'Бидний тухай' },
  title: { en: 'Restoring Hope Through Christ-Centered Care', mn: 'Христэд төвлөрсөн халамжаар найдварыг сэргээх' },
  descr:
    { en: 'Your partner and guide on the journey of a lifetime — discovering your purpose, embracing your calling, and finding what makes life truly worth living.', mn: 'Амьдралын хамгийн том аялалд таны түнш, хөтөч — зорилгоо нээж, дуудлагаа хүлээн авч, амьдралыг үнэхээр үнэ цэнтэй болгодог зүйлийг олох замд.' },
  /** Icons are inline SVG painted with tokens — see `royal-icons.tsx` for why. */
  blocks: [
    {
      icon: CROWN_ICON,
      title: { en: 'Our Mission', mn: 'Бидний эрхэм зорилго' },
      descr: { en: 'To equip people to live Christ-centered, abundant lives. (John 10:10)', mn: 'Хүмүүсийг Христэд төвлөрсөн, элбэг дэлбэг амьдралаар амьдрахад чадавхжуулах. (Иохан 10:10)' },
    },
    {
      icon: FLEUR_ICON,
      title: { en: 'Our Vision', mn: 'Бидний алсын хараа' },
      descr: { en: 'People healed, restored and set free, rising as servant leaders. (Isaiah 61:1)', mn: 'Эдгэрч, сэргэж, чөлөөлөгдсөн хүмүүс үйлчлэгч манлайлагч болон босох. (Исаиа 61:1)' },
    },
  ],
  ctaLabel: { en: 'Explore More', mn: 'Илүү ихийг үзэх' },
  ctaTo: '/about',
}

export const HOME_SERVICES_HEADING = {
  subTitle: { en: 'Mentoring, hospitality, enterprise and care', mn: 'Зөвлөгөө, зочломтгой байдал, аж ахуй, халамж' },
  title: { en: 'How We Walk Alongside People', mn: 'Бид хүмүүстэй хэрхэн хамт алхдаг вэ' },
}

/** All seven areas of work, once each — the slider loops, so it needs no padding. */
/** The first four areas of work, with the words still bilingual. See `DONATIONS`. */
export const HOME_SERVICES = SERVICE_LIST.map((service) => ({
  ...service,
  variant: 'default' as const,
}))

export const HOME_BLOG_HEADING = {
  /** The source pads this string with spaces on both sides. */
  subTitle: ' Latest Blog ',
  title: { en: 'Stories From The Work', mn: 'Ажлаас гарсан түүхүүд' },
  ctaLabel: { en: 'All Blogs', mn: 'Бүх нийтлэл' },
  ctaTo: '/blog',
}

/* ────────────────────────────────────────────────────────────────────────────
 * The four asks that close the page.
 *
 * The home page used to end on a project grid, a photo call to action, a testimonial
 * slider and a second contact form — four sections, three of them placeholder, and none
 * of them asking a visitor to do anything the Foundation actually wants. These four
 * replace them, and each one is a real thing a reader can act on: book the grounds, come
 * and mentor, join Partners, or give.
 *
 * Order is deliberate — the two free offers first, then the two that ask something back.
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * The two information panels, rendered as the design system's `SplitPanels`.
 *
 * The left panel's `See More` leaves for the resort rather than pointing at the
 * Foundation's own contact page: the resort owns the grounds, the calendar and the
 * conversation about a booking, and sending a church leader to a general contact form
 * would add a hop for no reason.
 */
export const HOME_OFFERS = {
  left: {
    title: { en: 'Free Space For Your Church, Ministry Or Family', mn: 'Таны чуулган, номлол, гэр бүлд үнэгүй орон зай' },
    descr:
      { en: 'Churches, Christian organisations and families can book the resort for an event, a retreat or a weekend away at no cost, or at a fraction of it where a group cannot cover the whole.', mn: 'Чуулган, Христийн байгууллага, гэр бүлүүд арга хэмжээ, цугларалт, амралтын өдрийн аялалд амралтын газрыг үнэ төлбөргүй, эсвэл бүлэг бүгдийг нь даахгүй бол түүний багахан хэсгээр захиалж болно.' },
    list: [
      { en: 'Retreats, camps and youth weekends', mn: 'Цугларалт, зуслан, залуучуудын амралтын өдрүүд' },
      { en: 'Conferences, seminars and training days', mn: 'Хурал, семинар, сургалтын өдрүүд' },
      { en: 'Family gatherings and church events', mn: 'Гэр бүлийн цугларалт, чуулганы арга хэмжээ' },
    ],
    ctaLabel: { en: 'See More', mn: 'Илүү ихийг үзэх' },
    ctaHref: appUrl(LINKS, 'resort', '/enquire'),
  },
  right: {
    title: { en: 'Partner To Mentor And Invest In Youth', mn: 'Зөвлөж, залуучуудад хөрөнгө оруулахаар хамтрах' },
    descr:
      { en: 'Our youth programmes run on people, not on premises: someone who will teach a trade, sit with a teenager, or fund a place for a young person who has nothing to spend.', mn: 'Манай залуучуудын хөтөлбөр нь барилга биш, хүмүүс дээр тогтдог: мэргэжил заах, өсвөр насны хүүхэдтэй хамт суух, эсвэл зарцуулах юмгүй залуугийн байрыг санхүүжүүлэх хүн.' },
    list: [
      { en: 'Mentor a young person in your own trade', mn: 'Өөрийн мэргэжлээр залуу хүнийг чиглүүлэх' },
      { en: 'Teach on a training weekend', mn: 'Амралтын өдрийн сургалтад заах' },
      { en: 'Fund a place on a youth programme', mn: 'Залуучуудын хөтөлбөрт нэг байр санхүүжүүлэх' },
    ],
    ctaLabel: { en: 'See More', mn: 'Илүү ихийг үзэх' },
    ctaTo: '/contact',
  },
}

/** And the last word on the page: give, or come and do something. */
export const HOME_DONATE_CTA = {
  subTitle: { en: 'Donate and get involved', mn: 'Хандивлаж, оролцоорой' },
  title: { en: 'God Has A Plan For All Of Us', mn: 'Бурхан бид бүгдэд зориулсан төлөвлөгөөтэй' },
  lead:
    { en: 'Great big things for us to do. It is time for a new start — step towards that vision, knowing He will keep His promise.', mn: 'Бидний хийх агуу том зүйлс. Шинээр эхлэх цаг болжээ — Тэр амлалтаа биелүүлнэ гэдгийг мэдсээр тэр алсын хараа руу алхаарай.' },
  primaryCta: { label: { en: 'Donate Now', mn: 'Одоо хандивлах' }, to: '/donations' },
  secondaryCta: { label: { en: 'Get Involved', mn: 'Оролцох' }, to: '/contact' },
}
